import type { MatrixData } from '../components/MatrixTable'

import { getOfflineDb } from '../offline/dbFactory'
import { ensureOfflineReady } from '../offline/initOffline'
import { enqueueEntityChange } from '../offline/outbox/entityOutbox'
import { drainEntityOutboxNow } from '../offline/outbox/syncEntityOutboxNow'
import { api } from './api'
import {
  buildQueuedEntityMutationResult,
  type EntityMutationResult,
  type EntityMutationSyncState
} from './entityMutationSync'
import {
  cacheMatrix,
  cloneMatrix,
  getDefaultMatrixForSpecies,
  hasRecentLocalSfrMatrixEdit,
  loadCachedMatrix,
  markLocalSfrMatrixEdit
} from './sfrMatrix'

type LoadSfrMatrixOptions = {
  forceRefresh?: boolean
  preferRecentLocal?: boolean
}

export type LoadSfrMatrixResult = {
  matrix: MatrixData
  syncState: EntityMutationSyncState
  errorMessage: string | null
}

const isOnline = (): boolean => typeof navigator === 'undefined' || navigator.onLine

const getLocalMatrix = (species: string): MatrixData => (
  cloneMatrix(loadCachedMatrix(species) ?? getDefaultMatrixForSpecies(species))
)

export const readSfrMatrixSyncState = async (species: string): Promise<EntityMutationSyncState> => {
  await ensureOfflineReady()

  const pendingCount = await getOfflineDb().entityOutbox
    .where('[entityType+entityId]')
    .equals(['sfrMatrix', species])
    .count()

  if (pendingCount === 0) {
    return 'synced'
  }

  return isOnline() ? 'pending' : 'offline'
}

export const loadSfrMatrix = async (
  species: string,
  options: LoadSfrMatrixOptions = {}
): Promise<LoadSfrMatrixResult> => {
  const localMatrix = getLocalMatrix(species)
  const syncState = await readSfrMatrixSyncState(species)

  if (options.preferRecentLocal && hasRecentLocalSfrMatrixEdit(species)) {
    return {
      matrix: localMatrix,
      syncState,
      errorMessage: null
    }
  }

  if (syncState !== 'synced') {
    return {
      matrix: localMatrix,
      syncState,
      errorMessage: null
    }
  }

  try {
    const record = await api.sfrMatrix.get(species, { noCache: options.forceRefresh })
    const matrix = cloneMatrix(record.matrix as MatrixData)
    cacheMatrix(species, matrix)
    return {
      matrix,
      syncState: 'synced',
      errorMessage: null
    }
  } catch (error) {
    return {
      matrix: localMatrix,
      syncState: 'synced',
      errorMessage: error instanceof Error ? error.message : 'Failed to load SFR matrix'
    }
  }
}

export const saveSfrMatrix = async (
  species: string,
  matrix: MatrixData
): Promise<EntityMutationResult<MatrixData>> => {
  const nextMatrix = cloneMatrix(matrix)
  cacheMatrix(species, nextMatrix)
  markLocalSfrMatrixEdit(species)

  await ensureOfflineReady()
  await enqueueEntityChange({
    entityType: 'sfrMatrix',
    op: 'upsert',
    entityId: species,
    payload: {
      species,
      matrix: nextMatrix
    }
  })

  return buildQueuedEntityMutationResult(nextMatrix, {
    entityType: 'sfrMatrix',
    entityId: species,
    maxEntries: 25,
    maxPasses: 5
  })
}

export const retrySfrMatrixSync = async (species: string): Promise<EntityMutationSyncState> => {
  if (!isOnline()) {
    return 'offline'
  }

  try {
    await drainEntityOutboxNow({
      maxEntries: 25,
      maxPasses: 5
    })
  } catch (error) {
    console.warn('[sfrMatrixApi] failed to drain pending SFR matrix changes', error)
  }

  return readSfrMatrixSyncState(species)
}